/**
 * KB projection: two-pass retrieve → score → trim (Phase 3 Feature L).
 */

import {
  TEMPORAL_BUCKET_ORDER,
  EDGE_STRENGTH_CLAMP,
  FEATURE_STATUS,
  TRUST_TIERS,
  TRUST_VERIFICATION_MULTIPLIERS,
  VERIFICATION_STATUS,
  KB_BUNDLE_TOKEN_BUDGET,
} from './ddxTemporalTaxonomy.js';
import { mapDurationBucketToTemporal, createEmptyPatientContext } from './ddxTemporalExtractor.js';
import { synthesizeNextBestActions } from './ddxNextBestActions.js';
import { applyContextModifiers, applyContextModifiersToEdge } from './ddxContextModifiers.js';
import {
  CLINICAL_CONTEXT_PROFILE_IDS,
  getClinicalContextProfile,
  PREVALENCE_TIER_MULTIPLIERS,
} from './ddxContextProfiles.js';
import { getContextConfigVariant } from './ddxContextConfigs.js';
import {
  getContextExpansionDxIds,
  isPregnantOrPostpartumContext,
  PREGNANCY_CANT_MISS_DX_IDS,
  buildHighImpactContextAlertStrings,
} from './ddxPatientContextExpansion.js';

/** ~4 chars per token heuristic */
const CHARS_PER_TOKEN = 4;

/** MC-5 / Feature L: high-sensitivity “expected missing” only when sensitivity strictly exceeds this. */
export const MISSING_HIGH_SENSITIVITY_THRESHOLD = 0.7;

const MAX_MATCHED_FEATURES_IN_BUNDLE = 14;

/**
 * @param {Partial<import('./ddxTypes.js').DdxContextProfileConfigDocument> | null | undefined} overrides
 * @returns {{ scoringParameters: { prevalenceWeight: number, edgeStrengthWeight: number, stabilityThreshold: string }, bundleComposition: object }}
 */
export function mergeClinicalProfileDefaults(overrides) {
  const fallback = getClinicalContextProfile(CLINICAL_CONTEXT_PROFILE_IDS.EMERGENCY_DEPARTMENT);
  const scoring = overrides?.scoringParameters || {};
  const bundle = overrides?.bundleComposition || {};

  return {
    scoringParameters: {
      prevalenceWeight:
        typeof scoring.prevalenceWeight === 'number' ? scoring.prevalenceWeight : fallback.prevalenceWeight,
      edgeStrengthWeight:
        typeof scoring.edgeStrengthWeight === 'number' ? scoring.edgeStrengthWeight : fallback.edgeStrengthWeight,
      stabilityThreshold: typeof scoring.stabilityThreshold === 'string' ? scoring.stabilityThreshold : fallback.stabilityThreshold,
    },
    bundleComposition: {
      ...getContextConfigVariant('baseline'),
      ...bundle,
    },
  };
}

/**
 * @param {object} dxDoc
 * @param {string} clinicalContextProfileId
 * @param {{ prevalenceWeight?: number }} profile
 * @returns {number}
 */
export function resolvePrevalenceMultiplier(dxDoc, clinicalContextProfileId, profile) {
  const tierRaw = dxDoc?.prevalenceByContext?.[clinicalContextProfileId];
  const tier = typeof tierRaw === 'string' ? tierRaw : 'unknown';
  const raw = PREVALENCE_TIER_MULTIPLIERS[tier] ?? PREVALENCE_TIER_MULTIPLIERS.unknown;
  const weight = typeof profile?.prevalenceWeight === 'number' ? profile.prevalenceWeight : 0;
  return (1 - weight) + weight * raw;
}

/**
 * @param {string} s
 * @returns {string}
 */
export function stableBundleHash(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 33) ^ s.charCodeAt(i);
  }
  return `h_${(h >>> 0).toString(16)}`;
}

/**
 * @param {string | null | undefined} acuity
 * @param {string[] | null | undefined} edgeBuckets - null = all buckets
 * @returns {number}
 */
export function temporalFitScore(acuity, edgeBuckets) {
  if (!edgeBuckets || edgeBuckets.length === 0) return 1;
  const a = acuity || 'indeterminate';
  if (edgeBuckets.includes(a)) return 1;
  const ai = TEMPORAL_BUCKET_ORDER.indexOf(a);
  if (ai < 0) return 0.7;
  let best = 0.4;
  for (const b of edgeBuckets) {
    const bi = TEMPORAL_BUCKET_ORDER.indexOf(b);
    if (bi < 0) continue;
    const dist = Math.abs(ai - bi);
    if (dist === 1) best = Math.max(best, 0.7);
    if (dist === 0) best = 1;
  }
  return best;
}

/**
 * @param {number} v
 * @returns {number}
 */
export function clampEdgeStrength(v) {
  return Math.max(EDGE_STRENGTH_CLAMP.min, Math.min(EDGE_STRENGTH_CLAMP.max, v));
}

/**
 * Phase 3 Feature J: Tier 2 unverified cannot score above lowest Tier 1 verified in the same bundle.
 * @param {import('./ddxTypes.js').ScoredDiagnosis[]} candidates - mutated in place
 * @returns {import('./ddxTypes.js').ScoredDiagnosis[]}
 */
export function applyTier2UnverifiedHardFloor(candidates) {
  const list = candidates || [];
  const tier1Verified = list.filter(
    (c) =>
      Number(c.trustTier) === TRUST_TIERS.TIER_1 &&
      (c.verificationStatus === VERIFICATION_STATUS.VERIFIED || !c.verificationStatus)
  );
  if (tier1Verified.length === 0) return list;
  const lowestTier1 = Math.min(...tier1Verified.map((c) => c.adjustedScore || 0));
  const cap = lowestTier1 - 0.01;
  for (const c of list) {
    if (Number(c.trustTier) === TRUST_TIERS.TIER_2 && c.verificationStatus === VERIFICATION_STATUS.UNVERIFIED) {
      if ((c.adjustedScore || 0) > cap) c.adjustedScore = cap;
    }
  }
  return list;
}

/**
 * @param {object} edge
 * @param {string | null} patientAgeBand
 * @returns {{ contribution: number, caveat: string | null }}
 */
export function edgeDemographicAdjustment(edge, patientAgeBand) {
  const mods = Array.isArray(edge.demographicModifiers) ? edge.demographicModifiers : [];
  if (!patientAgeBand) return { contribution: 0, caveat: null };
  for (const m of mods) {
    const bands = Array.isArray(m.ageBands) ? m.ageBands : [];
    if (bands.includes(patientAgeBand) && typeof m.strengthAdjustment === 'number') {
      return {
        contribution: m.strengthAdjustment,
        caveat: m.note ? String(m.note) : null,
      };
    }
  }
  return { contribution: 0, caveat: null };
}

/**
 * @param {object} dx
 * @returns {number}
 */
export function trustMultiplierForDiagnosis(dx) {
  const tier = dx.trustTier != null ? Number(dx.trustTier) : TRUST_TIERS.TIER_1;
  const ver =
    dx.verificationStatus === VERIFICATION_STATUS.UNVERIFIED
      ? VERIFICATION_STATUS.UNVERIFIED
      : VERIFICATION_STATUS.VERIFIED;
  if (tier === TRUST_TIERS.TIER_1 && ver === VERIFICATION_STATUS.VERIFIED) {
    return TRUST_VERIFICATION_MULTIPLIERS['1_verified'];
  }
  if (tier === TRUST_TIERS.TIER_2 && ver === VERIFICATION_STATUS.VERIFIED) {
    return TRUST_VERIFICATION_MULTIPLIERS['2_verified'];
  }
  if (tier === TRUST_TIERS.TIER_2 && ver === VERIFICATION_STATUS.UNVERIFIED) {
    return TRUST_VERIFICATION_MULTIPLIERS['2_unverified'];
  }
  return TRUST_VERIFICATION_MULTIPLIERS['2_verified'];
}

/**
 * Feature L Pass 2: modest boost when several independent edges fire.
 * @param {number} matchedEdgeCount
 * @returns {number} additive factor applied as base * (1 + bonus)
 */
export function multiFeatureBonus(matchedEdgeCount) {
  const n = Number(matchedEdgeCount) || 0;
  if (n <= 1) return 0;
  return Math.min(0.12, 0.03 * (n - 1));
}

/**
 * Penalize missing high-sensitivity rule-out-if-absent features (MC-5 sensitivity > 0.70).
 * @param {string[]} missingHighFeatureIds
 * @param {object[]} allEdgesForDx
 * @returns {number}
 */
export function computeMissingFeaturePenalty(missingHighFeatureIds, allEdgesForDx) {
  let p = 0;
  for (const fid of missingHighFeatureIds || []) {
    const edge = (allEdgesForDx || []).find((e) => e.featureId === fid);
    const s = Number(edge?.sensitivity) || 0;
    if (s > MISSING_HIGH_SENSITIVITY_THRESHOLD) {
      p += 0.045 * s;
    }
  }
  return Math.min(p, 0.2);
}

/**
 * @param {import('./ddxTypes.js').TemporalExtractionResult} extraction
 * @returns {{ supportingIds: Set<string>, absentIds: Set<string>, resolvedIds: Set<string> }}
 */
export function buildExtractionFeatureSets(extraction) {
  const supportingIds = new Set();
  const absentIds = new Set();
  const resolvedIds = new Set();
  for (const kf of extraction?.keyFeatures || []) {
    if (!kf.featureId) continue;
    if (kf.featureStatus === FEATURE_STATUS.PRESENT || kf.featureStatus === FEATURE_STATUS.HISTORICAL) {
      supportingIds.add(kf.featureId);
    } else if (kf.featureStatus === FEATURE_STATUS.ABSENT) absentIds.add(kf.featureId);
    else if (kf.featureStatus === FEATURE_STATUS.RESOLVED) resolvedIds.add(kf.featureId);
  }
  return { supportingIds, absentIds, resolvedIds };
}

/**
 * MC-9: apply edge absenceAdjustment only for explicitly absent features — not resolved/unknown/present.
 * Uses context modifier absenceAdjustmentOverride when present (least-penalizing merge is per-edge in applyContextModifiersToEdge).
 * @param {object[]} allForDx
 * @param {Set<string>} absentIds
 * @param {Set<string>} resolvedIds
 * @param {import('./ddxTypes.js').PatientContextProfile} [patientContext]
 * @returns {number}
 */
export function sumAbsenceAdjustmentsForDiagnosis(allForDx, absentIds, resolvedIds, patientContext) {
  const pc = patientContext || createEmptyPatientContext();
  let s = 0;
  for (const edge of allForDx || []) {
    if (!edge?.featureId || !absentIds?.has(edge.featureId)) continue;
    if (resolvedIds?.has(edge.featureId)) continue;
    if (edge.direction === 'contradicts') continue;
    const { absenceAdjustmentOverride } = applyContextModifiersToEdge(edge, pc);
    const base = Number(edge.absenceAdjustment);
    const adj =
      absenceAdjustmentOverride != null && Number.isFinite(Number(absenceAdjustmentOverride))
        ? Number(absenceAdjustmentOverride)
        : base;
    if (Number.isFinite(adj)) s += adj;
  }
  return s;
}

/**
 * @typedef {Object} KbProjectionDeps
 * @property {(id: string) => Promise<object | null>} getChiefComplaint
 * @property {(id: string) => Promise<object | null>} getDiagnosis
 * @property {(id: string) => Promise<object | null>} getFeature
 * @property {(ccData: object) => Promise<object[]>} getEdgesForChiefComplaint
 * @property {(args: { extraction: import('./ddxTypes.js').TemporalExtractionResult, chiefComplaintId: string, k?: number }) => Promise<string[]>} [searchSimilarDiagnoses] Feature L vector source (optional)
 */

/**
 * Pass 1 — collect diagnosis ids to score (edges + priority + can’t-miss + optional vector).
 * @param {string} chiefComplaintId
 * @param {import('./ddxTypes.js').TemporalExtractionResult} extraction
 * @param {KbProjectionDeps} deps
 * @param {{ includeTier2?: boolean, enableVectorDiagnosisSearch?: boolean, vectorSearchK?: number, clinicalContextProfileId?: import('./ddxTypes.js').ClinicalContextProfileId, contextConfigId?: string, clinicalContextOverrides?: Partial<import('./ddxTypes.js').DdxContextProfileConfigDocument> }} [options]
 * @returns {Promise<object>}
 */
export async function retrieveKnowledgeBundleContext(chiefComplaintId, extraction, deps, options = {}) {
  const includeTier2 = Boolean(options.includeTier2);
  const clinicalContextProfileId =
    options.clinicalContextProfileId || CLINICAL_CONTEXT_PROFILE_IDS.EMERGENCY_DEPARTMENT;
  const profileDefaults = getClinicalContextProfile(clinicalContextProfileId);
  const mergedContextProfile = {
    ...profileDefaults,
    ...mergeClinicalProfileDefaults(options.clinicalContextOverrides).scoringParameters,
  };
  const contextConfigId = options.contextConfigId || 'baseline';
  const contextConfig = {
    ...getContextConfigVariant(contextConfigId),
    ...mergeClinicalProfileDefaults(options.clinicalContextOverrides).bundleComposition,
  };
  const ccRaw = await deps.getChiefComplaint(chiefComplaintId);
  if (!ccRaw) {
    return {
      chiefComplaintId,
      extraction: extraction || {},
      empty: true,
      cc: null,
      kbVersion: '0',
      priorityDxIds: [],
      cantMissDxIds: [],
      acuity: extraction.acuityEstimate || 'indeterminate',
      patientAgeBand: extraction.demographics?.ageBand || null,
      supportingIds: new Set(),
      absentIds: new Set(),
      resolvedIds: new Set(),
      allEdges: [],
      candidateDxIds: [],
      vectorDxIds: [],
      clinicalContextProfileId,
      contextConfigId,
      mergedContextProfile,
      contextConfig,
    };
  }

  const cc = { id: chiefComplaintId, ...ccRaw };
  const kbVersion = cc.kbVersion != null ? String(cc.kbVersion) : '1.0.0';
  const priorityDxIds = Array.isArray(cc.priorityDxIds) ? cc.priorityDxIds : [];
  const cantMissBase = Array.isArray(cc.cantMissDxIds) ? cc.cantMissDxIds : [];
  const patientContext = extraction?.patientContext || createEmptyPatientContext();
  const cantMissDxIds = [...cantMissBase];
  if (isPregnantOrPostpartumContext(patientContext)) {
    for (const id of PREGNANCY_CANT_MISS_DX_IDS) {
      if (id && !cantMissDxIds.includes(id)) cantMissDxIds.push(id);
    }
  }

  const { supportingIds, absentIds, resolvedIds } = buildExtractionFeatureSets(extraction);
  const acuity =
    extraction.acuityEstimate ||
    mapDurationBucketToTemporal(extraction.keyFeatures?.[0]?.durationBucket) ||
    'indeterminate';
  const patientAgeBand = extraction.demographics?.ageBand || null;

  const allEdges = await deps.getEdgesForChiefComplaint(cc);
  const candidateDxIds = new Set();

  for (const e of allEdges) {
    if (!supportingIds.has(e.featureId)) continue;
    if (e.direction && e.direction === 'contradicts') continue;
    if (!includeTier2 && Number(e.trustTier) === TRUST_TIERS.TIER_2 && e.verificationStatus === VERIFICATION_STATUS.UNVERIFIED) {
      continue;
    }
    if (e.diagnosisId) candidateDxIds.add(e.diagnosisId);
  }

  for (const pid of priorityDxIds) candidateDxIds.add(pid);
  for (const cid of cantMissDxIds) candidateDxIds.add(cid);
  for (const exId of getContextExpansionDxIds(patientContext)) {
    if (exId) candidateDxIds.add(exId);
  }

  /** @type {string[]} */
  let vectorDxIds = [];
  const wantVector =
    Boolean(options.enableVectorDiagnosisSearch) &&
    typeof deps.searchSimilarDiagnoses === 'function' &&
    (typeof process === 'undefined' || process.env?.REACT_APP_DDX_VECTOR_RETRIEVAL === 'true' || process.env?.DDX_VECTOR_RETRIEVAL === 'true');
  if (wantVector) {
    try {
      const k = typeof options.vectorSearchK === 'number' ? options.vectorSearchK : 8;
      vectorDxIds = await deps.searchSimilarDiagnoses({
        extraction,
        chiefComplaintId,
        k,
      });
      for (const vid of vectorDxIds) {
        if (vid) candidateDxIds.add(vid);
      }
    } catch {
      vectorDxIds = [];
    }
  }

  return {
    chiefComplaintId,
    extraction,
    empty: false,
    cc,
    kbVersion,
    priorityDxIds,
    cantMissDxIds,
    acuity,
    patientAgeBand,
    supportingIds,
    absentIds,
    resolvedIds,
    allEdges,
    includeTier2,
    candidateDxIds: [...candidateDxIds],
    vectorDxIds,
    clinicalContextProfileId,
    contextConfigId,
    mergedContextProfile,
    contextConfig,
  };
}

/**
 * Pass 2 — score retrieved candidates and assemble bundle fields (before trim / next-best-actions).
 *
 * ## Scoring formula (per diagnosis with ≥1 matched supporting edge)
 *
 * Factors are applied in this order (all multiplicative unless noted):
 *
 * 1. **Per-edge base contributions** (summed into `base`): For each matched edge,
 *    `clampEdgeStrength(edgeStrength + context strength delta) × temporalFitScore(acuity, edge.temporalBuckets) × mergedContextProfile.edgeStrengthWeight`.
 *    Temporal fit is **folded in here**, not a separate top-level multiplier (diverges from a naive
 *    “five-factor chain” doc — clinically, timing gates each edge’s contribution).
 * 2. **Multi-feature bonus**: `baseAdj = base × (1 + multiFeatureBonus(n))` where bonus is in **[0, 0.12]**.
 * 3. **Absence adjustments**: add `sumAbsenceAdjustmentsForDiagnosis(...)` (can be negative or positive per edge rules).
 * 4. **Missing high-sensitivity penalty**: subtract `computeMissingFeaturePenalty` (capped at **0.2**), then
 *    `baseAdj = max(0.001, baseAdj - penalty)`.
 * 5. **Trust / verification**: multiply by `trustMultiplierForDiagnosis(dxDoc)` — values from
 *    `TRUST_VERIFICATION_MULTIPLIERS` (**~0.55–1.0**).
 * 6. **Conditioned prior (KB field)**: if `dxDoc.conditionedPriorProbability` is in (0,1], multiply by
 *    **`0.72 + 0.28 × prior`** (blend range **0.72–1.0**).
 * 7. **Context prior multiplier**: multiply by `applyContextModifiers(dxDoc).priorMultiplier` (dx-level context modifiers).
 * 8. **Prevalence (Phase optimization)**: multiply by `resolvePrevalenceMultiplier(...)` **unless** the diagnosis is
 *    can’t-miss (`cantMissDxIds` or `dxDoc.cantMiss`) — **hard bypass** so prevalence never suppresses
 *    surfacing (Phase 1 MC-7 / framework: can’t-miss must remain visible; prevalence affects ranking among
 *    non-bypassed candidates). Tier-2 unverified floor (**MC-2**) is applied **after** this loop via
 *    `applyTier2UnverifiedHardFloor`.
 *
 * **No matched edges**: a small positive score from absence sum and context `priorMultiplier` only (see early branch).
 *
 * **Winning condition**: higher `adjustedScore` sorts first; bundle trim keeps all can’t-miss rows regardless of score floor.
 *
 * @param {Awaited<ReturnType<typeof retrieveKnowledgeBundleContext>>} ctx
 * @param {KbProjectionDeps} deps
 * @param {{ includeTier2?: boolean, clinicalContextProfileId?: import('./ddxTypes.js').ClinicalContextProfileId, contextConfigId?: string }} [options]
 * @returns {Promise<import('./ddxTypes.js').DdxKnowledgeBundle>}
 */
export async function scoreKnowledgeBundleFromContext(ctx, deps, options = {}) {
  const includeTier2 = Boolean(options.includeTier2 ?? ctx.includeTier2);
  if (ctx.empty || !ctx.cc) {
    const pcEmpty = ctx.extraction?.patientContext || createEmptyPatientContext();
    return {
      chiefComplaintId: ctx.chiefComplaintId,
      temporalBucket: ctx.acuity || 'indeterminate',
      kbVersion: '0',
      candidates: [],
      cantMiss: [],
      expectedButMissing: [],
      bundleHash: stableBundleHash(`${ctx.chiefComplaintId}|empty`),
      nextBestActions: [],
      patientContext: pcEmpty,
      activeContextWarnings: [...(pcEmpty.activeContextWarnings || [])],
      highImpactContextFlags: buildHighImpactContextAlertStrings(pcEmpty),
      clinicalContextProfileId:
        options.clinicalContextProfileId ||
        ctx.clinicalContextProfileId ||
        CLINICAL_CONTEXT_PROFILE_IDS.EMERGENCY_DEPARTMENT,
      contextConfigId: options.contextConfigId || ctx.contextConfigId || 'baseline',
    };
  }

  const {
    chiefComplaintId,
    cc,
    kbVersion,
    priorityDxIds,
    cantMissDxIds,
    acuity,
    supportingIds,
    absentIds,
    resolvedIds,
    allEdges,
    candidateDxIds,
    clinicalContextProfileId,
    contextConfigId,
    mergedContextProfile,
    contextConfig,
  } = ctx;

  const patientContext = ctx.extraction?.patientContext || createEmptyPatientContext();
  const bundleModifierNotes = [];

  /** @type {Map<string, object[]>} */
  const edgesByDx = new Map();
  for (const e of allEdges) {
    if (!supportingIds.has(e.featureId)) continue;
    if (e.direction && e.direction === 'contradicts') continue;
    const dxId = e.diagnosisId;
    if (!dxId) continue;
    if (!includeTier2 && Number(e.trustTier) === TRUST_TIERS.TIER_2 && e.verificationStatus === VERIFICATION_STATUS.UNVERIFIED) {
      continue;
    }
    if (!edgesByDx.has(dxId)) edgesByDx.set(dxId, []);
    edgesByDx.get(dxId).push(e);
  }

  /** @type {Map<string, { score: number, edges: object[], missingHigh: string[], caveats: string[] }>} */
  const dxAgg = new Map();

  for (const dxId of candidateDxIds) {
    const dxEdges = edgesByDx.get(dxId) || [];
    let base = 0;
    const matched = [];
    const caveats = [];
    for (const edge of dxEdges) {
      if (!supportingIds.has(edge.featureId)) continue;
      const edgeCtx = applyContextModifiersToEdge(edge, patientContext);
      for (const n of edgeCtx.triggeredNotes) {
        bundleModifierNotes.push(n);
        caveats.push(n);
      }
      const contribution = edgeCtx.strengthAdjustment;
      const clamped = clampEdgeStrength(Number(edge.edgeStrength) + contribution);
      const tFit = temporalFitScore(acuity, edge.temporalBuckets);
      const edgeContribution = clamped * tFit * (mergedContextProfile?.edgeStrengthWeight || 1);
      base += edgeContribution;
      matched.push({
        edgeId: edge.id,
        featureId: edge.featureId,
        edgeStrength: Number(edge.edgeStrength) || 0,
        clampedContribution: edgeContribution,
        clinicalSignificance: edge.clinicalSignificance || 'rule_in',
        direction: edge.direction || 'supports',
      });
    }

    const allForDx = allEdges.filter((e) => e.diagnosisId === dxId);
    const missingHigh = [];
    for (const edge of allForDx) {
      if (edge.clinicalSignificance === 'rule_out_if_absent' && Number(edge.sensitivity) > MISSING_HIGH_SENSITIVITY_THRESHOLD) {
        if (!supportingIds.has(edge.featureId) && !resolvedIds.has(edge.featureId) && !absentIds.has(edge.featureId)) {
          missingHigh.push(edge.featureId);
        }
      }
    }

    const dxDoc = await deps.getDiagnosis(dxId);
    if (!dxDoc) continue;

    const dxMod = applyContextModifiers(dxDoc, patientContext);
    for (const n of dxMod.triggeredNotes) {
      bundleModifierNotes.push(n);
      caveats.push(n);
    }

    const absenceSum = sumAbsenceAdjustmentsForDiagnosis(allForDx, absentIds, resolvedIds, patientContext);

    if (matched.length === 0) {
      let score0 = Math.max(0.001, 0.01 + absenceSum);
      score0 *= dxMod.priorMultiplier;
      dxAgg.set(dxId, {
        score: score0,
        edges: [],
        missingHigh,
        caveats,
        dxDoc,
      });
      continue;
    }

    const mBonus = multiFeatureBonus(matched.length);
    let baseAdj = base * (1 + mBonus) + absenceSum;
    const missPen = computeMissingFeaturePenalty(missingHigh, allForDx);
    baseAdj = Math.max(0.001, baseAdj - missPen);

    const tm = trustMultiplierForDiagnosis(dxDoc);
    let adjustedScore = baseAdj * tm;

    const prior = dxDoc.conditionedPriorProbability;
    if (typeof prior === 'number' && prior > 0 && prior <= 1) {
      adjustedScore *= 0.72 + 0.28 * prior;
    }
    adjustedScore *= dxMod.priorMultiplier;
    const prevalenceBypass = cantMissDxIds.includes(dxId) || dxDoc?.cantMiss === true;
    if (!prevalenceBypass) {
      adjustedScore *= resolvePrevalenceMultiplier(dxDoc, clinicalContextProfileId, mergedContextProfile);
    }

    dxAgg.set(dxId, {
      score: adjustedScore,
      edges: matched,
      missingHigh,
      caveats,
      dxDoc,
    });
  }

  for (const pid of priorityDxIds) {
    if (!dxAgg.has(pid)) {
      const dxDoc = await deps.getDiagnosis(pid);
      if (dxDoc) {
        const pm = applyContextModifiers(dxDoc, patientContext).priorMultiplier;
        dxAgg.set(pid, {
          score: Math.max(0.001, 0.01 * pm),
          edges: [],
          missingHigh: [],
          caveats: [],
          dxDoc,
        });
      }
    }
  }

  /** @type {import('./ddxTypes.js').ScoredDiagnosis[]} */
  const candidates = [];
  for (const [dxId, agg] of dxAgg) {
    const d = agg.dxDoc;
    const isCantMiss = cantMissDxIds.includes(dxId);
    candidates.push({
      diagnosisId: dxId,
      label: d.label || d.name || dxId,
      adjustedScore: agg.score,
      temporalFitScore: 1,
      matchedFeatures: agg.edges,
      missingHighSensitivityFeatures: [...new Set(agg.missingHigh)],
      isCantMiss,
      trustTier: d.trustTier != null ? Number(d.trustTier) : TRUST_TIERS.TIER_1,
      verificationStatus: d.verificationStatus || VERIFICATION_STATUS.VERIFIED,
    });
  }

  applyTier2UnverifiedHardFloor(candidates);
  candidates.sort((a, b) => b.adjustedScore - a.adjustedScore);

  /** @type {import('./ddxTypes.js').CantMissEntry[]} */
  const cantMiss = [];
  for (const dxId of cantMissDxIds) {
    const d = await deps.getDiagnosis(dxId);
    if (!d) continue;
    const inCand = candidates.find((c) => c.diagnosisId === dxId);
    cantMiss.push({
      diagnosisId: dxId,
      label: d.label || d.name || dxId,
      rationale: d.cantMissRationale || 'Can’t-miss consideration',
      hasKbSupport: Boolean(inCand && inCand.matchedFeatures.length > 0),
    });
  }

  /** @type {import('./ddxTypes.js').ExpectedButMissing[]} */
  const expectedButMissing = [];
  for (const c of candidates) {
    for (const fid of c.missingHighSensitivityFeatures) {
      const feat = await deps.getFeature(fid);
      const edge = allEdges.find((e) => e.diagnosisId === c.diagnosisId && e.featureId === fid);
      let demoNote = null;
      if (edge) {
        const notes = applyContextModifiersToEdge(edge, patientContext).triggeredNotes;
        demoNote = notes.length ? notes[0] : null;
      }
      expectedButMissing.push({
        diagnosisId: c.diagnosisId,
        featureId: fid,
        featureLabel: feat?.label || fid,
        sensitivity: edge ? Number(edge.sensitivity) || 0 : 0,
        clinicalSignificance: edge?.clinicalSignificance || 'rule_out_if_absent',
        demographicCaveatNote: demoNote,
      });
    }
  }

  const ext = ctx.extraction || {};
  const hashPayload = JSON.stringify({
    cc: chiefComplaintId,
    v: kbVersion,
    f: (ext.keyFeatures || []).map((k) => [k.featureId, k.featureStatus]).sort(),
    pc: {
      p: patientContext.pregnancyStatus,
      i: patientContext.immunosuppressionStatus,
      fh: patientContext.flaggedHighImpactFactors,
    },
  });
  const bundleHash = stableBundleHash(hashPayload);

  const activeContextWarnings = [
    ...new Set([...bundleModifierNotes, ...(patientContext.activeContextWarnings || [])]),
  ];
  const highImpactContextFlags = buildHighImpactContextAlertStrings(patientContext);

  return {
    chiefComplaintId,
    temporalBucket: acuity,
    chiefComplaint: cc,
    kbVersion,
    candidates,
    cantMiss,
    expectedButMissing,
    bundleHash,
    nextBestActions: [],
    patientContext,
    activeContextWarnings,
    highImpactContextFlags,
    clinicalContextProfileId,
    contextConfigId,
    contextConfig,
  };
}

/**
 * @param {string} chiefComplaintId
 * @param {import('./ddxTypes.js').TemporalExtractionResult} extraction
 * @param {KbProjectionDeps} deps
 * @param {{ includeTier2?: boolean, enableVectorDiagnosisSearch?: boolean, vectorSearchK?: number, clinicalContextProfileId?: import('./ddxTypes.js').ClinicalContextProfileId, contextConfigId?: string, clinicalContextOverrides?: Partial<import('./ddxTypes.js').DdxContextProfileConfigDocument> }} [options]
 * @returns {Promise<import('./ddxTypes.js').DdxKnowledgeBundle>}
 */
export async function projectKnowledgeBundle(chiefComplaintId, extraction, deps, options = {}) {
  const ctx = await retrieveKnowledgeBundleContext(chiefComplaintId, extraction, deps, options);
  let bundle = await scoreKnowledgeBundleFromContext(ctx, deps, options);
  bundle.nextBestActions = synthesizeNextBestActions(bundle);
  bundle = trimBundleToTokenBudget(bundle);
  return bundle;
}

/**
 * Feature L: trim order — drop very low scores (except can’t-miss), cap expectedButMissing per dx, shorten verbose arrays, then size budget.
 * @param {import('./ddxTypes.js').DdxKnowledgeBundle} bundle
 * @returns {import('./ddxTypes.js').DdxKnowledgeBundle}
 */
export function trimBundleToTokenBudget(bundle) {
  const cantMissIds = new Set((bundle.cantMiss || []).map((x) => x.diagnosisId));
  const SCORE_FLOOR = 0.15;

  let candidates = (bundle.candidates || []).filter(
    (c) => c.isCantMiss || cantMissIds.has(c.diagnosisId) || (c.adjustedScore || 0) >= SCORE_FLOOR
  );
  candidates = candidates.map((c) => ({
    ...c,
    matchedFeatures: Array.isArray(c.matchedFeatures) ? c.matchedFeatures.slice(0, MAX_MATCHED_FEATURES_IN_BUNDLE) : c.matchedFeatures,
  }));
  candidates.sort((a, b) => b.adjustedScore - a.adjustedScore);

  let exp = capExpectedButMissingPerDiagnosis(bundle.expectedButMissing || [], 3);

  const maxChars = KB_BUNDLE_TOKEN_BUDGET * CHARS_PER_TOKEN;
  const weigh = () =>
    JSON.stringify({
      c: candidates,
      e: exp,
      m: bundle.cantMiss,
    }).length;

  let size = weigh();
  if (size <= maxChars) {
    return { ...bundle, candidates, expectedButMissing: exp };
  }

  const trimmed = [...candidates].filter((c) => c.isCantMiss || cantMissIds.has(c.diagnosisId));
  let rest = candidates.filter((c) => !c.isCantMiss && !cantMissIds.has(c.diagnosisId));
  rest.sort((a, b) => b.adjustedScore - a.adjustedScore);
  for (const c of rest) {
    trimmed.push(c);
    candidates = trimmed;
    size = weigh();
    if (size <= maxChars) break;
  }

  while (weigh() > maxChars && exp.length > 0) {
    exp = exp.slice(0, -1);
  }

  let c2 = candidates;
  while (weigh() > maxChars && c2.length > cantMissIds.size) {
    const dropIdx = c2.findIndex((x) => !x.isCantMiss && !cantMissIds.has(x.diagnosisId));
    if (dropIdx < 0) break;
    c2 = c2.filter((_, i) => i !== dropIdx);
    candidates = c2;
  }

  return { ...bundle, candidates: c2, expectedButMissing: exp };
}

/**
 * @param {import('./ddxTypes.js').ExpectedButMissing[]} rows
 * @param {number} maxPerDx
 * @returns {import('./ddxTypes.js').ExpectedButMissing[]}
 */
export function capExpectedButMissingPerDiagnosis(rows, maxPerDx) {
  /** @type {Map<string, import('./ddxTypes.js').ExpectedButMissing[]>} */
  const byDx = new Map();
  for (const row of rows || []) {
    const k = row.diagnosisId || '_';
    if (!byDx.has(k)) byDx.set(k, []);
    byDx.get(k).push(row);
  }
  const out = [];
  for (const [, list] of byDx) {
    const sorted = [...list].sort((a, b) => (b.sensitivity || 0) - (a.sensitivity || 0));
    out.push(...sorted.slice(0, maxPerDx));
  }
  return out;
}

/**
 * Benchmark helper: run retrieve + score without next-best / trim (for timing core pipeline).
 * @param {string} chiefComplaintId
 * @param {import('./ddxTypes.js').TemporalExtractionResult} extraction
 * @param {KbProjectionDeps} deps
 * @param {object} [options]
 */
export async function runKnowledgeBundleRetrieveAndScore(chiefComplaintId, extraction, deps, options = {}) {
  const ctx = await retrieveKnowledgeBundleContext(chiefComplaintId, extraction, deps, options);
  return scoreKnowledgeBundleFromContext(ctx, deps, options);
}
